import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
} from "@mui/material";

import PgHomeCompliancePanel from "./PgHomeCompliancePanel";
import IndemnityMarkingDialogContent from "./IndemnityMarkingDialogContent";

/**
 * Mark indemnity flow: choice dialog → offline confirm or PDF upload → compliance results.
 */
export default function PgHomeIndemnityDialogs({
  indemnityDialogOpen,
  closeIndemnityDialog,
  selectedBondForIndemnity,
  indemnityOption,
  setIndemnityOption,
  selectedFiles,
  isSubmitting,
  markingIndemnityBondId,
  validationOpen,
  setValidationOpen,
  validationResults,
  hasBlockingErrors,
  hasPiiWarningsOnly,
  handleMarkIndemnityOffline,
  handleSubmitIndemnityFiles,
  handleFinalizeIndemnitySubmit,
  isFinalizing,
  handleFilesSelected,
  handleClearFiles,
  handleRemoveFile,
  handleViewFile,
  processingOpen,
}) {
  return (
    <>
      <Dialog open={indemnityDialogOpen} onClose={closeIndemnityDialog} maxWidth="md" fullWidth>
        <DialogTitle>Mark Indemnity as Completed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            How would you like to mark this indemnity as completed?
          </Typography>

          <IndemnityMarkingDialogContent
            indemnityOption={indemnityOption}
            setIndemnityOption={setIndemnityOption}
            selectedBondForIndemnity={selectedBondForIndemnity}
            markingBondId={markingIndemnityBondId}
            handleMarkIndemnityOffline={handleMarkIndemnityOffline}
            selectedFiles={selectedFiles}
            handleFilesSelected={handleFilesSelected}
            handleClearFiles={handleClearFiles}
            handleRemoveFile={handleRemoveFile}
            handleViewFile={handleViewFile}
            handleSubmitIndemnityFiles={handleSubmitIndemnityFiles}
            isSubmitting={isSubmitting}
            onBackFromUpload={handleClearFiles}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={processingOpen} slotProps={{ paper: { sx: { textAlign: "center", p: 3 } } }}>
        <DialogContent>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Processing Your Submission
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Thank you for submitting the Indemnity Agreement. Please wait a moment as the Indemnity Agreement is
            being screened for submission.
          </Typography>
        </DialogContent>
      </Dialog>

      <PgHomeCompliancePanel
        open={validationOpen}
        onClose={() => setValidationOpen(false)}
        validationResults={validationResults}
        hasBlockingErrors={hasBlockingErrors}
        hasPiiWarningsOnly={hasPiiWarningsOnly}
        onFinalize={handleFinalizeIndemnitySubmit}
        isFinalizing={isFinalizing}
      />
    </>
  );
}

PgHomeIndemnityDialogs.propTypes = {
  indemnityDialogOpen: PropTypes.bool.isRequired,
  closeIndemnityDialog: PropTypes.func.isRequired,
  selectedBondForIndemnity: PropTypes.object,
  indemnityOption: PropTypes.oneOf(["offline", "upload"]),
  setIndemnityOption: PropTypes.func.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  markingIndemnityBondId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  validationOpen: PropTypes.bool.isRequired,
  setValidationOpen: PropTypes.func.isRequired,
  validationResults: PropTypes.array.isRequired,
  hasBlockingErrors: PropTypes.bool.isRequired,
  hasPiiWarningsOnly: PropTypes.bool.isRequired,
  handleMarkIndemnityOffline: PropTypes.func.isRequired,
  handleSubmitIndemnityFiles: PropTypes.func.isRequired,
  handleFinalizeIndemnitySubmit: PropTypes.func.isRequired,
  isFinalizing: PropTypes.bool.isRequired,
  handleFilesSelected: PropTypes.func.isRequired,
  handleClearFiles: PropTypes.func.isRequired,
  handleRemoveFile: PropTypes.func.isRequired,
  handleViewFile: PropTypes.func.isRequired,
  processingOpen: PropTypes.bool.isRequired,
};
